import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceBalanceData } from '../types';
import { widgetDate } from './widgetFormat';

type Props = {
  data: AbsenceBalanceData;
};

const AbsenceBalanceWidget: React.FC<React.PropsWithChildren<Props>> = ({
  data,
}) => {
  const { t } = useTranslation();
  return (
    <div className="ai-chat-widget ai-chat-widget-absence">
      {data.categories.map((cat, idx) => {
        const remaining = cat.total - cat.used;
        const pct = cat.total > 0 ? (remaining / cat.total) * 100 : 0;
        const resets = data.resetDate ? (
          <span>
            {t('aiChat.widgets.absence.resets', {
              date: widgetDate(data.resetDate, data.resetDate),
            })}
          </span>
        ) : null;

        // An unlimited category has no allowance, so remaining, total and the
        // progress bar are all 0 — which reads as "nothing left", the opposite
        // of what it means (FLA-8212). Suppress them; days taken still counts.
        if (cat.unlimited) {
          return (
            <div key={idx} className="ai-chat-widget-absence-category">
              <div className="ai-chat-widget-absence-header">{cat.name}</div>
              <div className="ai-chat-widget-absence-stats">
                <div className="ai-chat-widget-absence-stat remaining">
                  <span className="ai-chat-widget-absence-stat-value">
                    {t('aiChat.widgets.absence.unlimited')}
                  </span>
                </div>
                <div className="ai-chat-widget-absence-stat used">
                  <span className="ai-chat-widget-absence-stat-value">
                    {cat.used}
                  </span>
                  <span className="ai-chat-widget-absence-stat-label">
                    {t('aiChat.widgets.absence.used')}
                  </span>
                </div>
              </div>
              <div className="ai-chat-widget-absence-footer">
                <span>{t('aiChat.widgets.absence.noAnnualLimit')}</span>
                {resets}
              </div>
            </div>
          );
        }

        return (
          <div key={idx} className="ai-chat-widget-absence-category">
            <div className="ai-chat-widget-absence-header">{cat.name}</div>
            <div className="ai-chat-widget-absence-stats">
              <div className="ai-chat-widget-absence-stat remaining">
                <span className="ai-chat-widget-absence-stat-value">
                  {remaining}
                </span>
                <span className="ai-chat-widget-absence-stat-label">
                  {t('aiChat.widgets.absence.remaining')}
                </span>
              </div>
              <div className="ai-chat-widget-absence-stat used">
                <span className="ai-chat-widget-absence-stat-value">
                  {cat.used}
                </span>
                <span className="ai-chat-widget-absence-stat-label">
                  {t('aiChat.widgets.absence.used')}
                </span>
              </div>
              <div className="ai-chat-widget-absence-stat total">
                <span className="ai-chat-widget-absence-stat-value">
                  {cat.total}
                </span>
                <span className="ai-chat-widget-absence-stat-label">
                  {t('aiChat.widgets.absence.total')}
                </span>
              </div>
            </div>
            <div className="ai-chat-widget-absence-bar-bg">
              <div
                className="ai-chat-widget-absence-bar-fill"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="ai-chat-widget-absence-footer">
              <span>
                {t('aiChat.widgets.absence.percentRemaining', {
                  percent: Math.round(pct),
                })}
              </span>
              {resets}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AbsenceBalanceWidget;
